import { ProductDetailsComponent } from '../../components/order/product-details/product-details.component';
import { Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { DateTime } from 'luxon';
import { Order, ProductItem, Size } from 'src/app/interfaces/orders';
import { OrderSaveSource } from 'src/app/interfaces/order-save-source';
import { repairOutgoingOrder } from 'src/app/interfaces/order-payload-guard';
import { buildOrderDiff } from 'src/app/interfaces/order-diff';
import { DataServerService } from '../api/data-server.service';
import { SubscriberService } from './subscriber.service';
import { OfflineService } from './offline.service';
import { AlertService } from '../base/alert.service';
import { SessionService } from './session.service';
import { CoreService } from './core.service';

@Injectable({
  providedIn: 'root',
})
export class OrderService {
  private syncTimer: ReturnType<typeof setTimeout> | null = null;
  private syncStartedAt: number | null = null;

  private readonly SYNC_INTERVAL_FAST = 20_000;
  private readonly SYNC_INTERVAL_SLOW = 3 * 60_000;
  private readonly SYNC_FAST_DURATION = 5 * 60_000;

  constructor(
    private modalController: ModalController,
    private dataServer: DataServerService,
    private sub: SubscriberService,
    private offline: OfflineService,
    private alert: AlertService,
    private session: SessionService,
    private core: CoreService
  ) {
    this.sub.syncRequested.subscribe(() => {
      this.syncOfflineOrders(true).then(() => {
        this.core.refreshOrders();
      });
    });

    this.sub.offlineOrders.subscribe(orders => {
      if (orders && orders.length > 0) {
        this.startRetryLoop();
      } else {
        this.stopRetryLoop();
      }
    });
  }

  private startRetryLoop() {
    if (this.syncTimer !== null) return;
    this.syncStartedAt = Date.now();
    this.scheduleNextSync();
  }

  private stopRetryLoop() {
    if (this.syncTimer !== null) {
      clearTimeout(this.syncTimer);
      this.syncTimer = null;
    }
    this.syncStartedAt = null;
  }

  private scheduleNextSync() {
    const elapsed = Date.now() - (this.syncStartedAt ?? Date.now());
    const interval = elapsed < this.SYNC_FAST_DURATION
      ? this.SYNC_INTERVAL_FAST
      : this.SYNC_INTERVAL_SLOW;

    this.syncTimer = setTimeout(async () => {
      this.syncTimer = null;
      await this.syncOfflineOrders();
      await this.core.refreshOrders();
      // refreshOrders() cleans up synced orders — check if any remain
      const remaining = this.sub.offlineOrders.getValue();
      if (remaining && remaining.length > 0) {
        this.scheduleNextSync();
      } else {
        this.stopRetryLoop();
      }
    }, interval);
  }

  // Manage Orders
  async updateOrCreateOrder(order: Order, saveSource: OrderSaveSource = 'unbekannt') {
    // Letzte Prüfung vor dem Absenden: Felder, die hier leer sind, aber lokal noch Daten
    // haben, werden aus dem lokalen Stand aufgefüllt. Ein solcher Request geht damit gar
    // nicht erst raus — er kann also auch nicht verspätet einen guten Serverstand
    // überschreiben. Das Backend prüft dasselbe nochmals als letztes Netz.
    const known = await this.offline.findPersistedOrder(order.orderId);
    let fullOrder: Order = { ...order };
    const repair = repairOutgoingOrder(fullOrder, known);
    if (repair.repairedFields.length > 0) {
      fullOrder = repair.order;
      console.error(
        `[repairOutgoingOrder] Unvollständiger Auftrag vor dem Senden korrigiert — ` +
        `Order ${order.orderNumber ?? order.orderId} | Auslöser: ${saveSource} | ` +
        `Felder: ${repair.repairedFields.join(', ')}`,
      );
    }

    // Nur die tatsächlich geänderten Felder senden. Was nicht mitgeht, kann serverseitig
    // auch nicht überschrieben werden — das beseitigt die Ursache des Datenverlusts.
    // Die Vergleichsbasis ist der zuletzt vom Server bestätigte Stand.
    const baseline = await this.offline.getSyncBaseline(order.orderId);
    const diff = buildOrderDiff(fullOrder, baseline);

    if (baseline && diff.changedFields.length === 0) {
      // Nichts geändert — kein Request nötig. Der Auftrag gilt weiterhin als synchron.
      await this.offline.markOfflineOrderSynced(fullOrder);
      return;
    }

    const payload: Record<string, unknown> = {
      ...diff.payload,
      revision: fullOrder.revision,
    };

    // If owner is "NEW", remove it so the server can set the correct owner
    if (payload['owner'] === 'NEW') {
      delete payload['owner'];
    }

    const upsert = await this.dataServer.upsertOrder(payload, saveSource);

    if (upsert.status === 200 || upsert.status === 201) {
      // Die vom Server vergebene Revision übernehmen — sonst würde der nächste Save
      // fälschlich als Konflikt gewertet und unnötig zusammengeführt.
      const serverOrder = (upsert as any)?.data?.order;
      const serverRevision = serverOrder?.revision;
      if (typeof serverRevision === 'number') {
        fullOrder.revision = serverRevision;
        this.applyRevisionLocally(order.orderId, serverRevision);
      }

      // Bestätigter Erfolg — Order aus offlineOrders in syncedOfflineOrders verschieben (nie löschen).
      // Lokal wird IMMER der vollständige Auftrag abgelegt, nie nur die Teiländerung.
      await this.offline.markOfflineOrderSynced(fullOrder);
      await this.offline.setSyncBaseline(fullOrder);
      await this.core.refreshOrders();
    } else if (upsert.status === 401) {
      // 401 wird bereits global von ApiService.handleUnauthorized() behandelt
      // (Relogin-Dialog + automatischer Re-Sync via sub.requestSync()) — hier nichts weiter tun,
      // Order bleibt unverändert in offlineOrders.
      console.error('Failed to upsert order (unauthorized):', upsert.status);
    } else {
      console.error('Failed to upsert order:', upsert.status);
      const errMsg = (upsert as any)?.data?.error ?? `HTTP ${upsert.status}`;
      this.alert.presentInfoAlert(
        'Auftrag konnte nicht synchronisiert werden',
        `Der Auftrag bleibt lokal gespeichert und wird erneut versucht.\n\nFehler: ${errMsg}`
      );
    }

  }

  /**
   * Trägt die vom Server vergebene Revision auf den aktuellen lokalen Objekten nach.
   * Bewusst als Feldzuweisung auf dem bestehenden Objekt und nicht als Ersetzen durch
   * eine Kopie — parallel entstehende Zweitkopien waren die Ursache des Datenverlusts.
   */
  private applyRevisionLocally(orderId: string, revision: number): void {
    const current = this.sub.currentOrder.getValue();
    if (current?.orderId === orderId) {
      current.revision = revision;
    }
    const offline = this.sub.offlineOrders.getValue().find(o => o.orderId === orderId);
    if (offline) {
      offline.revision = revision;
    }
  }

  async syncOfflineOrders(forceAll: boolean = false, saveSource: OrderSaveSource = 'retryLoop') {

    const offlineOrders = await this.sub.offlineOrders.getValue();
    const currtenOrder = await this.sub.currentOrder.getValue();

    for (const order of offlineOrders) {
      if (
        currtenOrder &&
        order.orderId === currtenOrder.orderId &&
        forceAll === false
      ) {
        // Skip the current order
        continue;
      }

      await this.updateOrCreateOrder(order, saveSource);

      await new Promise((resolve) => setTimeout(resolve, 2000));
    }
  }

  // Change Order

  async openProductModal(product: any) {
    let copy = structuredClone(product);
    const modal = await this.modalController.create({
      component: ProductDetailsComponent,
      componentProps: { product: copy },
      cssClass: 'modal-full',
    });

    modal.onDidDismiss().then((result) => {
      if (result.data) {
        const updatedProduct = result.data;
        // Hier kannst du das aktualisierte Objekt an einen Service weitergeben
        this.addProductsToOrder(updatedProduct);
      }
    });

    return await modal.present();
  }

  addProductsToOrder(_product: any) {
    // stub — modal result handler, not yet implemented
  }

  async createDiliveryNote(order: Order) {
    //const dorder = this.transformOrderToDelivery(order);

    const session = await this.session.greenlightSession();
    if (!session) {
      this.alert.presentInfoAlert(
        'Session abgelaufen',
        'Beleg kann nicht erstellt werden'
      );
      return;
    }


    const dorder = this.bypassFormOrderToDelivery(order);


    const resd = await this.dataServer.postLexwareDelivery(dorder);
    if (resd.status === 200 || resd.status === 201) {
      this.alert.presentToast('Lieferschein erfolgreich an Lexware gesendet');
    } else {
      this.alert.presentInfoAlert(
        'Fehler beim Senden des Lieferscheins',
        resd.data
      );
    }

    return;
  }

  calculateTotalQuantity(order: Order): number {
    if (!order?.products) {
      return 0;
    }

    return order.products.reduce((total: number, product: ProductItem) => {
      const productQuantity = product.sizes?.reduce((sum: number, size: Size) => sum + (size.quantity || 0), 0) || 0;
      return total + productQuantity;
    }, 0);
  }


  bypassFormOrderToDelivery(order: any): any {
    const sessionToken = this.session.token;
    const heute = DateTime.now()
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
      .toISO({ includeOffset: false });
    const formattedDateTime = heute + 'Z';

    const items = this.convertToLineItemsINV(order.products);
    const totals = this.calculateTotalQuantity(order);
    const totaltext = "Gesamtstückzahl: " + totals;

    const voucher: any = {
      mask: {
        alternativeItems: false,
        deliveryTerms: true,
        downPaymentDeductions: false,
        englishPrintLayout: true,
        epcCode: false,
        expirationDate: false,
        freeTextItems: true,
        identificationNumber: true,
        immutableItemList: false,
        invoiceIdentificationNumber: false,
        itemDiscount: false,
        itemPrice: false,
        optionalItems: false,
        paymentOptionsActive: false,
        paymentOptionsSupported: false,
        paymentTerms: false,
        paypalMe: false,
        sharedLink: false,
        shippingDate: true,
        title: true,
        totalAmount: false,
        totalReduction: false,
        voucherDate: true,
        xrechnungSupported: false,
      },
      organizationId: sessionToken.organizationId,
      version: 0,
      voucher: {
        address: {
          city: order.customerAddress.city || '',
          countryAlpha2Code: 'DE',
          countryId: 4,
          country: 'Deutschland',
          listTypeId: '38b485a0-07d2-488b-9bb2-0e6ebb531296',
          name: order.customerAddress.name || '',
          street: order.customerAddress.street || '',
          supplement: order.customerAddress.supplement || '',
          zip: order.customerAddress.zip || '',
        },
        archived: false,
        aspects: [],
        billingStatus: 'draft',
        claimedGrossAmount: 0,
        constructionServiceNote:
          'Steuerschuldnerschaft des Leistungsempfängers (Reverse Charge)',
        contactId: order.customerBase.contactId,
        contactVersion: 3,
        deliveryTerms: '',
        downPaymentDeductions: [],
        externalServiceNote:
          'Steuerschuldnerschaft des Leistungsempfängers (Reverse Charge)',
        intraCommunitySupplyNote:
          'Steuerfreie innergemeinschaftliche Lieferung',
        introduction: '',
        //invoiceIdentificationNumber: 'RE0001',
        isPayable: false,
        items: items,
        language: 'de',
        oneTimeContact: false,
        //paymentRange: 0,
        //organizationPaymentTermsId: '88c9d6a8-393d-4219-9512-57dc5c0a11e1',
        //paymentTerms: '',
        //relatedVouchers: [
        //{
        //  salesVoucherId: 'b52c713f-34b2-4141-9a2f-71c9d4f045d0',
        //   voucherType: 'Invoice',
        // },
        //],
        remark: totaltext,
        shippingDate: formattedDateTime, //'2025-01-23T00:00:00.000Z',
        shippingType: 'Delivery',
        taxType: 'net',
        thirdPartyCountryDeliveryNote: 'Steuerfreie Ausfuhrlieferung',
        thirdPartyCountryServiceNote: 'Nicht im Inland steuerbare Leistung',
        title: 'Lieferschein',
        totalReductionType: 'NONE',
        vatFreeNote: '',
        voucherDate: formattedDateTime,
        voucherType: 'DeliveryNote',
      },
    };
    return voucher;
  }

  bypassFormOrderToInvoice(order: any): any {
    const sessionToken = this.session.token;
    const heute = DateTime.now()
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
      .toISO({ includeOffset: false });
    const formattedDateTime = heute + 'Z';

    const totalQuantityAll = this.calculateTotalQuantity(order);
    const orderNumber = order?.orderNumber ?? '';
    const note = order?.note ?? '';
    const baseRemark = `Auftrag: ${orderNumber} | Gesamt: ${totalQuantityAll}`;
    const remark = note ? `${baseRemark} | Note: ${note}` : baseRemark;


    const items = this.convertToLineItemsREC(order.products);

    const invoice: any = {
      voucher: {
        address: {
          city: order.customerAddress.city || '',
          countryAlpha2Code: 'DE',
          country: 'Deutschland',
          listTypeId: '38b485a0-07d2-488b-9bb2-0e6ebb531296',
          name: order.customerAddress.name || '',
          street: order.customerAddress.street || '',
          supplement: order.customerAddress.supplement || '',
          zip: order.customerAddress.zip || '',
          email: order.customerBase.email || '',
          phone: order.customerBase.phone || '',
        },
        delivery: {
          city: order.customerBase.delivery.city || '',
          countryAlpha2Code: 'DE',
          country: 'Deutschland',
          listTypeId: '38b485a0-07d2-488b-9bb2-0e6ebb531296',
          name: order.customerBase.delivery.companyName || '',
          street: order.customerBase.delivery.street || '',
          supplement: order.customerBase.delivery.supplement || '',
          zip: order.customerBase.delivery.zip || '',
        },
        contactId: order.customerBase.contactId,
        externalServiceNote: "Steuerschuldnerschaft des Leistungsempfängers (Reverse Charge)",
        intraCommunitySupplyNote: "Steuerfreie innergemeinschaftliche Lieferung",
        introduction: "Unsere Lieferungen/Leistungen stellen wir Ihnen wie folgt in Rechnung.",
        items: items,
        paymentRange: 30,
        paymentTerms: '10 Tage 4 % Skonto - 30 Tage ohne Abzug ',
        remark: remark,
        shippingDate: formattedDateTime,
        shippingType: 'Delivery',
        taxType: 'net',
        thirdPartyCountryDeliveryNote: '',
        thirdPartyCountryServiceNote: '',
        title: 'Rechnung',
        voucherDate: formattedDateTime,
      },
    };


    return invoice;
  }

  convertToLineItemsREC(items: ProductItem[]) {
    return items.map((item: ProductItem, index: number) => {
      const names = `${item.model} ${item.name}`;

      const sizeDetails = item.sizes
        .filter((size: Size) => size.quantity > 0)
        .map((size: Size) => `${size.size}: ${size.quantity}`)
        .join(' - ');

      const totalQuantity = item.sizes.reduce(
        (sum: number, size: Size) => sum + (Number(size?.quantity) || 0),
        0
      );

      const formattedPrice = parseFloat(String(item.price)).toFixed(2);

      return {
        description: sizeDetails
          ? `${item.color} ${item.colorname} | ${sizeDetails} | Gesamt: ${totalQuantity}`
          : `${item.color} ${item.colorname} | Gesamt: ${totalQuantity}`,
        discountPercentage: 0,
        grossPrice: 0,
        active: true,
        itemTemplateVersion: 0,
        itemType: 'custom_item',
        name: names,
        netPrice: formattedPrice ,
        positionNumber: (index + 1).toString(), // Laufende Nummer pro Element
        priceDecimalPlaces: 2,
        quantity: item.sizes.reduce(
          (sum: number, size: Size) => sum + size.quantity,
          0
        ),
        taxRateI18nKey: 'USt19',
        taxRateId: 'c4405267-98f6-488f-9bb8-9ae96acbc2f6',
        taxRatePercentage: 19,
        unitId: '2a1aec1a-1a57-4be7-a1ec-9f224180a07d',
        unitName: 'Stück',
      };
    });
  }

  convertToLineItemsINV(items: ProductItem[]) {
    return items.map((item: ProductItem, index: number) => {
      const names = `${item.model} ${item.name}`;

      const sizeDetails = item.sizes
        .filter((size: Size) => size.quantity > 0)
        .map((size: Size) => `${size.size}: ${size.quantity}`)
        .join(' - ');

      const totalQuantity = item.sizes.reduce(
        (sum: number, size: Size) => sum + (Number(size?.quantity) || 0),
        0
      );

      return {
        description: sizeDetails
          ? `${item.color} ${item.colorname} | ${sizeDetails} | Gesamt: ${totalQuantity}`
          : `${item.color} ${item.colorname} | Gesamt: ${totalQuantity}`,
        discountPercentage: 0,
        grossPrice: 0,
        active: true,
        itemTemplateVersion: 0,
        itemType: 'custom_item',
        name: names,
        netPrice: 0,
        positionNumber: (index + 1).toString(),
        priceDecimalPlaces: 2,
        quantity: item.sizes.reduce(
          (sum: number, size: Size) => sum + size.quantity,
          0
        ),
        taxRateI18nKey: 'USt19',
        taxRateId: 'c4405267-98f6-488f-9bb8-9ae96acbc2f6',
        taxRatePercentage: 19,
        unitId: '2a1aec1a-1a57-4be7-a1ec-9f224180a07d',
        unitName: 'Stück',
      };
    });
  }

  /**
   * Baut die Bestaetigung nach dem Karma-Uebertrag: Stueckzahl und Betraege im
   * direkten Vergleich Kontor ↔ Karma. Nur so laesst sich erkennen, ob wirklich
   * alles angekommen ist.
   *
   * `verification.expectedCost` ist der Einkaufspreis (Summe aus Menge x cost) —
   * das ist der Wert, der zum Karma-Betrag passen muss. Der Verkaufspreis wird
   * zusaetzlich aus der Order berechnet.
   */
  private buildKarmaSummary(data: any, warnings: string[]): string {
    const v = data.verification;
    const lines: string[] = [];

    lines.push(`Warenkorb: ${data.cartLabel ?? '–'}`);

    if (v) {
      const qtyOk = v.quantityMatch;
      const qtyMark = qtyOk ? '✓' : '⚠';
      lines.push('');
      lines.push(`Stückzahl:`);
      lines.push(`  Kontor: ${v.expectedQuantity} Stk.`);
      lines.push(`  Karma: ${v.karmaQuantity ?? '?'} Stk. ${qtyMark}`);

      const costMark = v.costMatch ? '✓' : '⚠';
      lines.push('');
      lines.push(`Einkauf (Kosten):`);
      lines.push(`  Kontor: ${v.expectedCost}`);
      lines.push(`  Karma: ${v.karmaAmount ?? '?'} ${costMark}`);
    }

    if (this.lastKarmaSalesTotal !== null) {
      lines.push('');
      lines.push(`Verkauf (Order):`);
      lines.push(`  ${this.lastKarmaSalesTotal.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);
    }

    const allWarnings = [...warnings, ...(v?.warning ? [v.warning] : [])];
    if (allWarnings.length) {
      lines.push('');
      lines.push(`Hinweise:`);
      allWarnings.forEach(w => lines.push(`  ${w}`));
    }

    return lines.join('\n');
  }

  /** Verkaufssumme der zuletzt uebertragenen Order (fuer die Karma-Bestaetigung). */
  private lastKarmaSalesTotal: number | null = null;

  /**
   * Zählt Order-Positionen, die aktuell nicht im Karma-Katalog verfügbar sind
   * (Snapshot-Flag oder Live-Abgleich gegen die aktuelle Produktliste — analog
   * zum Lookup in der Orderübersicht), um vor der B2B-Übertragung zu warnen.
   */
  private countKarmaUnavailableProducts(order: Order): number {
    const productList = this.sub.productList.getValue() ?? [];
    const lookup = new Map<string, boolean>();
    for (const p of productList) {
      const unavailable = p?.karmaUnavailable === true;
      if (p?.karmaId) lookup.set(`id:${p.karmaId}`, unavailable);
      if (p?.model && p?.colorname) lookup.set(`mc:${p.model}::${p.colorname}`, unavailable);
    }

    return (order.products ?? []).filter((p: any) => {
      if (p?.karmaUnavailable === true) return true;
      if (p?.karmaId && lookup.has(`id:${p.karmaId}`)) return lookup.get(`id:${p.karmaId}`)!;
      if (p?.model && p?.colorname) {
        const key = `mc:${p.model}::${p.colorname}`;
        if (lookup.has(key)) return lookup.get(key)!;
      }
      return false;
    }).length;
  }

  /**
   * Prueft, ob diese Order bereits an Karma uebertragen wurde (karma-cart-log).
   * Waren ALLE bisherigen Versuche fehlerhaft, gilt das nicht als Duplikat —
   * dann ohne Rueckfrage weitermachen. Gab es mindestens eine erfolgreiche
   * oder "bitte pruefen"-Uebertragung, muss der Nutzer die erneute Uebertragung
   * bewusst bestaetigen (Liste der bisherigen Versuche im Dialog).
   */
  private async confirmIfAlreadySentToKarma(order: Order): Promise<boolean> {
    if (!order?.orderId) return true;

    const res: any = await this.dataServer.getKarmaCartLogForOrder(order.orderId);
    const entries: any[] = res?.data?.docs ?? [];
    if (entries.length === 0) return true;

    const allFailed = entries.every(e => e.status === 'error');
    if (allFailed) return true;

    const lines: string[] = [`Für diesen Auftrag gibt es bereits ${entries.length} frühere Übertragung(en):`, ''];
    for (const e of entries) {
      const when = DateTime.fromISO(e.createdAt).toFormat('dd.MM.yyyy HH:mm');
      const statusLabel = e.status === 'ok' ? 'Erfolgreich' : e.status === 'warning' ? 'Bitte geprüft' : 'Fehler';
      lines.push(`${when} — ${statusLabel} (${e.cartLabel ?? '–'})`);
    }
    lines.push('');
    lines.push('Trotzdem erneut übertragen?');

    return this.alert.presentConfirmationAlert(
      'Bereits an Karma übertragen',
      lines.join('\n'),
    );
  }

  async sendToKarma(order: Order, kuerzelMode: 'user' | 'customer' = 'user', opts: {
    paymentId: string;
    deliveryDate: string;
    email: string;
    phone: string;
    note?: string;
  } = { paymentId: '59', deliveryDate: '', email: '', phone: '' }): Promise<{ label: string; url: string | null } | null> {
    if (!order.orderId) return null;

    if (!(await this.confirmIfAlreadySentToKarma(order))) return null;

    // Verkaufssumme der Order — das Backend liefert nur den Einkaufswert (cost).
    this.lastKarmaSalesTotal = (order.products ?? []).reduce((sum: number, p: any) => {
      const qty = (p.sizes ?? []).reduce((s: number, sz: any) => s + (Number(sz.quantity) || 0), 0);
      return sum + qty * (Number(p.price) || 0);
    }, 0);

    const res = await this.dataServer.sendToKarma(order.orderId, kuerzelMode, opts);
    const data = res?.data;

    if (res?.status === 200 && data?.success) {
      const karmaUnavailableCount = this.countKarmaUnavailableProducts(order);
      const warnings: string[] = [
        ...(data.missingKarmaId?.length ? [`${data.missingKarmaId.length} Artikel ohne Karma-ID übersprungen`] : []),
        ...(data.outdatedProducts?.length ? [`${data.outdatedProducts.length} Artikel aus alter Season nicht übertragen (nicht mehr im letzten Sync)`] : []),
        ...(data.rejectedByKarma?.length ? [`${data.rejectedByKarma.length} Artikel von Karma abgelehnt (nicht mehr verfügbar)`] : []),
        ...(karmaUnavailableCount ? [`${karmaUnavailableCount} Artikel in dieser Order sind aktuell nicht im Karma-Katalog verfügbar`] : []),
        ...(data.customerWarning ? [data.customerWarning] : []),
      ];

      // Bei Abweichungen: Confirmation-Dialog, damit der Nutzer bewusst bestätigen muss
      const hasWarnings = warnings.length || data.verification?.warning;
      const hasAbweichung = data.verification && (!data.verification.quantityMatch || !data.verification.costMatch);
      const hasDuplicate = !!data.duplicateOrder;

      if (hasAbweichung || hasDuplicate) {
        // Bestätigung erforderlich: Nutzer muss bewusst signalisieren, dass er die Abweichung/das Duplikat verstanden hat
        const confirmed = await this.alert.presentConfirmationAlert(
          hasDuplicate ? 'Mögliches Duplikat bei Karma gefunden' : 'Abweichung erkannt — bitte genau prüfen',
          this.buildKarmaSummary(data, warnings),
        );
        if (!confirmed) {
          this.alert.presentToast('Übertragung abgebrochen');
          return null;
        }
      } else if (hasWarnings) {
        // Nur Info-Alert bei sonstigen Hinweisen
        this.alert.presentInfoAlert(
          'Warenkorb erstellt — bitte prüfen',
          this.buildKarmaSummary(data, warnings),
        );
      } else {
        // Alles OK — kurzer Toast
        this.alert.presentToast('Warenkorb erstellt');
      }

      return data.cartLabel ? { label: data.cartLabel, url: data.cartUrl ?? null } : null;
    } else {
      const errMsg = data?.error ?? JSON.stringify(data ?? res);
      this.alert.presentInfoAlert('Fehler beim Übertragen zu Karma B2B', errMsg);
      return null;
    }
  }

  /**
   * Prueft, ob fuer diese Order bereits Teilrechnungen existieren. Eine Gesamtrechnung
   * trotz bestehender Teilrechnungen ist meist ein Versehen — daher Warnung vor dem
   * eigentlichen Uebertrag (sowohl fuer den Queue- als auch den Online-Weg).
   */
  private async confirmIfPartialInvoicesExist(order: Order): Promise<boolean> {
    if (!order?.orderId) return true;

    const res: any = await this.dataServer.getDeliveriesForOrder(order.orderId);
    const deliveries: any[] = res?.data?.docs ?? [];
    const invoicedCount = deliveries.filter(d => d.invoiceStatus === 'invoiced').length;

    if (invoicedCount === 0) return true;

    return this.alert.presentConfirmationAlert(
      'Teilrechnungen vorhanden',
      `Für diesen Auftrag existieren bereits ${invoicedCount} Teilrechnung(en). Trotzdem eine Gesamtrechnung erstellen?`,
    );
  }

  /** Markiert einen Auftrag manuell als "vollständig abgerechnet" — für Fälle, in denen ein
   * Rest nie geliefert/berechnet wird und der Prozentsatz sonst dauerhaft unter 100% bliebe.
   * Erstellt KEINE Rechnung und ändert invoicedNet/invoicedPercent NICHT — reine Anzeige-
   * Markierung. Warnt vor dem Setzen, da sie die Anzeige gegenüber der Realität übersteuert. */
  async closeOrderInvoicingManually(order: Order): Promise<boolean> {
    if (!order?.orderId) return false;

    const confirmed = await this.alert.presentConfirmationAlert(
      'Manuell als komplett abgerechnet markieren',
      'Dies erstellt KEINE Rechnung und bucht nichts — es blendet den Auftrag nur als "abgerechnet" ' +
      'ein, obwohl rechnerisch noch etwas offen ist (z.B. weil ein Rest nie geliefert wird). ' +
      'Nur setzen, wenn du sicher bist, dass hier nichts mehr abgerechnet werden muss.',
    );
    if (!confirmed) return false;

    try {
      const res: any = await this.dataServer.closeOrderInvoicing(order.orderId, true);
      if (!res?.data?.success) {
        this.alert.presentInfoAlert('Fehler', res?.data?.error ?? 'Markierung konnte nicht gespeichert werden');
        return false;
      }
      return true;
    } catch (e: any) {
      console.error('closeOrderInvoicingManually error:', e);
      this.alert.presentInfoAlert('Fehler', e.message || 'Unbekannter Fehler');
      return false;
    }
  }

  /** Überträgt den Auftrag an einen anderen Besitzer (nur Admin, der Server prüft die Rolle).
   * Ablauf: Benutzer wählen → Bestätigung → Server-Aufruf. Gibt true zurück, wenn der Besitzer
   * gewechselt wurde. Der Auftrag verschwindet danach aus der Liste des bisherigen Besitzers. */
  async changeOrderOwner(order: Order): Promise<boolean> {
    if (!order?.orderId) return false;

    const usersRes: any = await this.dataServer.getUsers();
    const users: any[] = usersRes?.status === 200 ? (usersRes.data?.docs ?? []) : [];
    if (users.length === 0) {
      this.alert.presentInfoAlert('Fehler', 'Die Benutzerliste konnte nicht geladen werden.');
      return false;
    }

    const displayName = (user: any): string => user.username || user.internalname || user.email;
    const currentOwnerId: string | undefined = typeof order.owner === 'object' ? order.owner?.id : undefined;
    const currentOwnerName = typeof order.owner === 'object'
      ? displayName(order.owner)
      : String(order.owner ?? 'unbekannt');

    const candidates = users.filter(user => user.id !== currentOwnerId);
    const newOwnerId = await this.alert.presentSelectAlert(
      'Besitzer wechseln',
      `Aktueller Besitzer: ${currentOwnerName}. An wen soll der Auftrag übertragen werden?`,
      candidates.map(user => ({
        label: `${displayName(user)}${user.role === 'admin' ? ' (Admin)' : ''}`,
        value: user.id,
      })),
    );
    if (!newOwnerId) return false;

    const newOwner = candidates.find(user => user.id === newOwnerId);
    const confirmed = await this.alert.presentConfirmationAlert(
      'Besitzer wirklich wechseln?',
      `Auftrag ${order.orderNumber} wird von „${currentOwnerName}“ an „${displayName(newOwner)}“ übertragen. ` +
      `„${currentOwnerName}“ sieht den Auftrag danach nicht mehr.`,
      'Übertragen',
    );
    if (!confirmed) return false;

    try {
      const res: any = await this.dataServer.changeOrderOwner(order.orderId, newOwnerId);
      if (!res?.data?.success) {
        this.alert.presentInfoAlert('Fehler', res?.data?.error ?? 'Besitzer konnte nicht gewechselt werden');
        return false;
      }

      // Feldzuweisung auf den bestehenden Objekten (keine Kopie), wie bei applyRevisionLocally.
      const current = this.sub.currentOrder.getValue();
      const offline = this.sub.offlineOrders.getValue().find(o => o.orderId === order.orderId);
      for (const target of [order, current?.orderId === order.orderId ? current : undefined, offline]) {
        if (target) target.owner = res.data.owner;
      }
      if (typeof res.data.revision === 'number') {
        this.applyRevisionLocally(order.orderId, res.data.revision);
        order.revision = res.data.revision;
      }
      // Sonst würde der geänderte Besitzer beim nächsten Speichern als Änderung mitgesendet.
      await this.offline.setSyncBaseline(order);
      await this.core.refreshOrders();

      this.alert.presentToast(`Auftrag an ${displayName(newOwner)} übertragen.`);
      return true;
    } catch (e: any) {
      console.error('changeOrderOwner error:', e);
      this.alert.presentInfoAlert('Fehler', e.message || 'Unbekannter Fehler');
      return false;
    }
  }

  /** Entfernt die manuelle "vollständig abgerechnet"-Markierung wieder — kein Warn-Dialog
   * nötig, da das Rückgängigmachen nichts verbirgt, sondern die reale Berechnung wiederherstellt. */
  async reopenOrderInvoicing(order: Order): Promise<boolean> {
    if (!order?.orderId) return false;
    try {
      const res: any = await this.dataServer.closeOrderInvoicing(order.orderId, false);
      if (!res?.data?.success) {
        this.alert.presentInfoAlert('Fehler', res?.data?.error ?? 'Markierung konnte nicht entfernt werden');
        return false;
      }
      return true;
    } catch (e: any) {
      console.error('reopenOrderInvoicing error:', e);
      this.alert.presentInfoAlert('Fehler', e.message || 'Unbekannter Fehler');
      return false;
    }
  }

  /**
   * Baut die Bestaetigung nach der Lexware-Uebertragung: Stueckzahl und Netto-Betrag
   * im direkten Vergleich Kontor ↔ Lexware, analog zu buildKarmaSummary.
   */
  private buildLexwareSummary(queueItem: any): string {
    const v = queueItem?.verification;
    const lines: string[] = [];

    lines.push(`Beleg: ${queueItem?.label ?? '–'}`);

    if (v) {
      lines.push('');
      lines.push(`Stückzahl (Kontor): ${v.expectedQuantity} Stk.`);

      const nettoMark = v.match ? '✓' : '⚠';
      lines.push('');
      lines.push(`Netto-Betrag:`);
      lines.push(`  Kontor: ${typeof v.expectedNetto === 'number' ? v.expectedNetto.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : v.expectedNetto} €`);
      lines.push(`  Lexware: ${v.lexwareNetto ?? '?'} ${nettoMark}`);
    }

    if (queueItem?.errorMessage) {
      lines.push('');
      lines.push(`Hinweise: ${queueItem.errorMessage}`);
    }

    return lines.join('\n');
  }

  /**
   * Pollt den Status eines lexware-queue Items in kurzen, flachen Intervallen (5s),
   * bis er nicht mehr 'pending' ist — oder bricht nach ~3 Minuten ab (der Nutzer
   * schaut aktiv zu, anders als beim unbeaufsichtigten Offline-Sync-Retry-Timer,
   * daher bewusst kein Fast/Slow-Escalation-Pattern wie bei syncOfflineOrders).
   */
  private async pollLexwareQueueItem(queueId: string): Promise<void> {
    const POLL_INTERVAL_MS = 5000;
    const MAX_ATTEMPTS = 36; // ~3 Minuten

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));

      const res: any = await this.dataServer.getLexwareQueueItem(queueId);
      const item = res?.data?.data?.[0];
      if (!item) continue;

      if (item.status === 'done') {
        this.alert.presentInfoAlert('Lexware-Übertragung abgeschlossen', this.buildLexwareSummary(item));
        return;
      }
      if (item.status === 'failed') {
        this.alert.presentInfoAlert('Lexware-Übertragung fehlgeschlagen', item.errorMessage ?? 'Unbekannter Fehler');
        return;
      }
    }

    this.alert.presentToast('Übertragung läuft noch — Status später in den Einstellungen prüfen');
  }

  /**
   * Prueft, ob fuer diesen Auftrag bereits frueher eine Lexware-Uebertragung mit exakt
   * derselben Stueckzahl und demselben Netto-Betrag ausgeloest wurde — egal ob noch
   * ausstehend, erfolgreich oder fehlgeschlagen. Die orderNumber ist durch sourceId
   * bereits fix (die Historie wird ueber dieselbe Order abgefragt); Stueckzahl+Betrag
   * sind das eigentliche Duplikat-Signal, da erst sie eine inhaltlich identische
   * Übertragung von einer echten Teil-/Korrektur-Uebertragung unterscheiden.
   *
   * Bewusst reine Frontend-Warnung (kein Backend-Block): Gibt es mindestens einen
   * frueheren Versuch mit gleicher Stueckzahl+Betrag, muss der Nutzer die erneute
   * Uebertragung bewusst bestaetigen und sieht dabei, wann diese bereits in die
   * Warteschlange eingereiht wurde — analog zu confirmIfAlreadySentToKarma.
   */
  private async confirmIfAlreadySentToLexware(order: Order): Promise<boolean> {
    if (!order?.orderId) return true;

    const totalQuantity = this.calculateTotalQuantity(order);
    const totalNet = (order.products ?? []).reduce((sum: number, p: any) => {
      const qty = (p.sizes ?? []).reduce((s: number, sz: any) => s + (Number(sz.quantity) || 0), 0);
      return sum + qty * (Number(p.price) || 0);
    }, 0);
    const roundedTotalNet = Number(totalNet.toFixed(2));

    const res: any = await this.dataServer.getLexwareTransferHistoryForSource(order.orderId);
    const entries: any[] = res?.data?.data ?? [];

    const matches = entries.filter((e: any) =>
      Number(e.totalQuantity) === totalQuantity &&
      Number(e.totalNet ?? 0).toFixed(2) === roundedTotalNet.toFixed(2)
    );
    if (matches.length === 0) return true;

    const lines: string[] = [
      `Für Auftrag ${order.orderNumber ?? order.orderId} wurde bereits ${matches.length}x mit derselben Stückzahl (${totalQuantity} Stk.) und demselben Betrag (${roundedTotalNet.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €) an Lexware übertragen:`,
      '',
    ];
    for (const e of matches) {
      const when = DateTime.fromISO(e.createdAt).toFormat('dd.MM.yyyy HH:mm');
      const statusLabel = e.status === 'done' ? 'Erfolgreich' : e.status === 'failed' ? 'Fehler' : 'Ausstehend';
      lines.push(`In Warteschlange gesetzt am ${when} — ${statusLabel} (${e.label ?? '–'})`);
    }
    lines.push('');
    lines.push('Trotzdem erneut an Lexware übertragen?');

    return this.alert.presentConfirmationAlert(
      'Mögliches Duplikat bei Lexware',
      lines.join('\n'),
    );
  }

  /** Reiht die Gesamtrechnung in die Lexware-Warteschlange ein; je nach LexwareSettings.transferMode
   * verarbeitet der Server-Runner sie automatisch oder sie wartet auf den manuellen Runner-Lauf. */
  async createInvoice(order: Order) {
    if (!order?.orderId) {
      this.alert.presentToast('Kein Auftrag gefunden');
      return;
    }

    if (!(await this.confirmIfPartialInvoicesExist(order))) return;
    if (!(await this.confirmIfAlreadySentToLexware(order))) return;

    const res: any = await this.dataServer.triggerLexwareTransfer('order', order.orderId);
    const data = res?.data;

    if (!data?.success) {
      this.alert.presentInfoAlert('Fehler', data?.error ?? 'Übertragung konnte nicht gestartet werden');
      return;
    }

    if (data.mode === 'manual') {
      this.alert.presentToast('In Warteschlange eingereiht — wird manuell verarbeitet');
      return;
    }

    this.alert.presentToast(
      data.spawned ? 'Online-Übertragung gestartet' : 'Übertragung läuft bereits — wird mitverarbeitet',
    );
    void this.pollLexwareQueueItem(data.queueId);
  }

}
